import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/store/features/baseRtkQuery';
import {
    DatabaseExplorerAccess,
    ExecuteQueryResponse,
    ExportRequest,
    GenerateQueryRequest,
    GenerateQueryResponse,
    FilterCondition,
    OrderByClause,
    TableInfo,
    TableQueryRequest,
    TableQueryResponse,
    TableSchema,
} from '@/services/models/DatabaseExplorer';

export const databaseExplorerApiSlice = createApi({
    reducerPath: 'databaseExplorerApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['DatabaseTable', 'DatabaseSchema'],
    endpoints: (builder) => ({
        checkAccess: builder.query<DatabaseExplorerAccess, void>({
            query: () => '/database-explorer/access',
        }),
        getTables: builder.query<TableInfo[], void>({
            query: () => '/database-explorer/tables',
            providesTags: ['DatabaseTable'],
        }),
        getTableSchema: builder.query<TableSchema, string>({
            query: (tableName) => `/database-explorer/tables/${encodeURIComponent(tableName)}/schema`,
            providesTags: (_result, _error, tableName) => [{ type: 'DatabaseSchema', id: tableName }],
        }),
        getTableCount: builder.query<number, string>({
            query: (tableName) => `/database-explorer/tables/${encodeURIComponent(tableName)}/count`,
        }),
        queryTable: builder.mutation<TableQueryResponse, { tableName: string; request: TableQueryRequest }>({
            query: ({ tableName, request }) => ({
                url: `/database-explorer/tables/${encodeURIComponent(tableName)}/query`,
                method: 'POST',
                body: request,
            }),
        }),
        insertRow: builder.mutation<number, { tableName: string; values: Record<string, unknown> }>({
            query: ({ tableName, values }) => ({
                url: `/database-explorer/tables/${encodeURIComponent(tableName)}/rows`,
                method: 'POST',
                body: { values },
            }),
            invalidatesTags: ['DatabaseTable'],
        }),
        updateRow: builder.mutation<
            number,
            { tableName: string; primaryKey: Record<string, unknown>; values: Record<string, unknown> }
        >({
            query: ({ tableName, primaryKey, values }) => ({
                url: `/database-explorer/tables/${encodeURIComponent(tableName)}/rows`,
                method: 'PUT',
                body: { primaryKey, values },
            }),
            invalidatesTags: ['DatabaseTable'],
        }),
        deleteRows: builder.mutation<number, { tableName: string; primaryKeys: Record<string, unknown>[] }>({
            query: ({ tableName, primaryKeys }) => ({
                url: `/database-explorer/tables/${encodeURIComponent(tableName)}/rows`,
                method: 'DELETE',
                body: { primaryKeys },
            }),
            invalidatesTags: ['DatabaseTable'],
        }),
        truncateTable: builder.mutation<{ rowsDeleted: number }, { tableName: string; confirmationTableName: string }>({
            query: ({ tableName, confirmationTableName }) => ({
                url: `/database-explorer/tables/${encodeURIComponent(tableName)}/truncate`,
                method: 'POST',
                body: { confirmationTableName },
            }),
            invalidatesTags: ['DatabaseTable'],
        }),
        executeQuery: builder.mutation<ExecuteQueryResponse, { query: string }>({
            query: ({ query }) => ({
                url: '/database-explorer/query/execute',
                method: 'POST',
                body: { query },
            }),
        }),
        generateQuery: builder.mutation<GenerateQueryResponse, GenerateQueryRequest>({
            query: (body) => ({
                url: '/database-explorer/query/generate',
                method: 'POST',
                body,
            }),
        }),
        exportData: builder.mutation<Blob, ExportRequest>({
            query: (body) => ({
                url: '/database-explorer/export',
                method: 'POST',
                body,
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const {
    useCheckAccessQuery,
    useGetTablesQuery,
    useGetTableSchemaQuery,
    useLazyGetTableSchemaQuery,
    useGetTableCountQuery,
    useQueryTableMutation,
    useInsertRowMutation,
    useUpdateRowMutation,
    useDeleteRowsMutation,
    useTruncateTableMutation,
    useExecuteQueryMutation,
    useGenerateQueryMutation,
    useExportDataMutation,
} = databaseExplorerApiSlice;

export type { FilterCondition, OrderByClause, TableQueryRequest };
